import { defineStore } from 'pinia'
import axios from '../config/api.js'

export const useEmailsStore = defineStore('emails', {
  state: () => ({
    emails: [],
    loading: false,
    syncing: false,
    error: null,
    stats: {
      waiting: 0,
      pending: 0,
      to_treat: 0,
      processed: 0,
      total: 0
    },
    filters: {
      status: '',
      accountId: null,
      dateFrom: null,
      dateTo: null,
      sender: '',
      content: ''
    },
    pagination: {
      page: 1,
      limit: 50,
      total: 0
    }
  }),

  getters: {
    emailsByStatus: (state) => (status) => {
      return state.emails.filter(email => email.status === status)
    },
    
    waitingEmails: (state) => {
      return state.emails.filter(email => email.status === 'waiting')
    },
    
    pendingEmails: (state) => {
      return state.emails.filter(email => email.status === 'pending')
    },
    
    toTreatEmails: (state) => {
      return state.emails.filter(email => email.status === 'to_treat')
    },
    
    processedEmails: (state) => {
      return state.emails.filter(email => email.status === 'processed')
    },
    
    unreadEmails: (state) => {
      return state.emails.filter(email => !email.read)
    },
    
    emailsWithAttachments: (state) => {
      return state.emails.filter(email => email.attachments.length > 0)
    },
    
    averageAiScore: (state) => {
      if (state.emails.length === 0) return 0
      const total = state.emails.reduce((sum, email) => sum + email.aiScore, 0)
      return Math.round(total / state.emails.length)
    },
    
    lowScoreEmails: (state) => {
      return state.emails.filter(email => email.aiScore < 60)
    }
  },

  actions: {
    async loadEmails(page = 1) {
      this.loading = true
      this.error = null
      
      try {
        // Appel API réel
        const response = await axios.get('/api/messages', {
          params: {
            page,
            limit: this.pagination.limit,
            ...this.filters
          }
        })
        
        // Gérer les deux formats de réponse: tableau direct ou objet { data, pagination }
        const payload = Array.isArray(response.data)
          ? response.data
          : (response.data?.data ?? [])

        // Mapper les données de l'API vers le format attendu par le frontend
        this.emails = payload.map(email => ({
          id: email.id,
          subject: email.subject,
          senderName: (email.fromEmail || '').split('@')[0],
          senderEmail: email.fromEmail,
          recipientEmail: email.toEmail,
          accountId: email.accountId ?? 1,
          receivedAt: email.date ? new Date(email.date) : null,
          status: email.status || 'waiting',
          read: email.parsed || false,
          aiScore: Math.floor(Math.random() * 100),
          preview: email.body ? email.body.substring(0, 100) + '...' : '',
          textContent: email.body || '',
          attachments: email.attachments ? email.attachments : [],
          hasCommande: !!email.commandes_count && email.commandes_count > 0,
          commandesCount: email.commandes_count || 0
        }))

        // Pagination: si fournie par l'API, l'utiliser; sinon fallback sur la taille du payload
        if (!Array.isArray(response.data) && response.data?.pagination) {
          this.pagination.total = response.data.pagination.total ?? payload.length
          this.pagination.page = response.data.pagination.page ?? page
          this.pagination.limit = response.data.pagination.limit ?? this.pagination.limit
        } else {
          this.pagination.total = payload.length
          this.pagination.page = page
        }
        this.updateStats()
        
      } catch (error) {
        this.error = 'Erreur lors du chargement des emails'
        console.error('Erreur loadEmails:', error)
      } finally {
        this.loading = false
      }
    },

    updateStats() {
      this.stats = {
        waiting: this.emails.filter(e => e.status === 'waiting').length,
        pending: this.emails.filter(e => e.status === 'pending').length,
        to_treat: this.emails.filter(e => e.status === 'to_treat').length,
        processed: this.emails.filter(e => e.status === 'processed').length,
        total: this.emails.length
      }
    },

    async syncEmails() {
      this.syncing = true
      this.error = null
      
      try {
        await axios.post('/api/emails/sync')
        
        // Recharger les emails après synchronisation
        await this.loadEmails()
        
      } catch (error) {
        this.error = 'Erreur lors de la synchronisation'
        console.error('Erreur syncEmails:', error)
      } finally {
        this.syncing = false
      }
    },

    async processEmailWithAI(emailId) {
      try {
        const response = await axios.post(`/api/emails/${emailId}/process`)
        
        const email = this.emails.find(e => e.id === emailId)
        if (email && response.data) {
          // Mettre à jour l'email avec les données de l'API
          Object.assign(email, response.data)
          this.updateStats()
        }
        
        return response.data
        
      } catch (error) {
        this.error = 'Erreur lors du traitement IA'
        console.error('Erreur processEmailWithAI:', error)
        throw error
      }
    },

    async markAsRead(emailId, read = true) {
      try {
        // Simulation d'appel API
        // await axios.patch(`/api/emails/${emailId}`, { read })
        
        await new Promise(resolve => setTimeout(resolve, 300))
        
        const email = this.emails.find(e => e.id === emailId)
        if (email) {
          email.read = read
        }
        
      } catch (error) {
        this.error = 'Erreur lors du marquage lu/non lu'
        console.error('Erreur markAsRead:', error)
        throw error
      }
    },

    async deleteEmail(emailId) {
      try {
        // Simulation d'appel API
        // await axios.delete(`/api/emails/${emailId}`)
        
        await new Promise(resolve => setTimeout(resolve, 500))
        
        const index = this.emails.findIndex(e => e.id === emailId)
        if (index !== -1) {
          this.emails.splice(index, 1)
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors de la suppression'
        console.error('Erreur deleteEmail:', error)
        throw error
      }
    },

    async searchEmails(query, filters = {}) {
      try {
        // Simulation d'appel API
        // const response = await axios.get('/api/emails/search', { params: { q: query, ...filters } })
        // return response.data
        
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Recherche locale pour la démo
        const results = this.emails.filter(email => {
          const searchText = `${email.subject} ${email.senderName} ${email.senderEmail} ${email.textContent}`.toLowerCase()
          const matchesQuery = !query || searchText.includes(query.toLowerCase())
          const matchesStatus = !filters.status || email.status === filters.status
          const matchesAccount = !filters.accountId || email.accountId === filters.accountId
          
          return matchesQuery && matchesStatus && matchesAccount
        })
        
        return results
        
      } catch (error) {
        this.error = 'Erreur lors de la recherche'
        console.error('Erreur searchEmails:', error)
        throw error
      }
    },

    async getEmailById(emailId) {
      try {
        // Vérifier d'abord dans le store local
        let email = this.emails.find(e => e.id === emailId)
        
        if (!email) {
          // Simulation d'appel API si pas trouvé localement
          // const response = await axios.get(`/api/emails/${emailId}`)
          // email = response.data
          
          await new Promise(resolve => setTimeout(resolve, 300))
          return null
        }
        
        return email
        
      } catch (error) {
        this.error = 'Erreur lors de la récupération de l\'email'
        console.error('Erreur getEmailById:', error)
        throw error
      }
    },

    async updateEmailStatus(emailId, status) {
      try {
        // Simulation d'appel API
        // await axios.patch(`/api/emails/${emailId}`, { status })
        
        await new Promise(resolve => setTimeout(resolve, 300))
        
        const email = this.emails.find(e => e.id === emailId)
        if (email) {
          email.status = status
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors de la mise à jour du statut'
        console.error('Erreur updateEmailStatus:', error)
        throw error
      }
    },

    async exportEmails(format = 'csv', filters = {}) {
      try {
        // Simulation d'appel API
        // const response = await axios.post('/api/emails/export', { format, filters })
        // return response.data
        
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        const filteredEmails = this.emails.filter(email => {
          if (filters.status && email.status !== filters.status) return false
          if (filters.accountId && email.accountId !== filters.accountId) return false
          if (filters.dateFrom && new Date(email.receivedAt) < new Date(filters.dateFrom)) return false
          if (filters.dateTo && new Date(email.receivedAt) > new Date(filters.dateTo)) return false
          return true
        })
        
        return {
          filename: `emails_${new Date().toISOString().split('T')[0]}.${format}`,
          data: filteredEmails,
          count: filteredEmails.length
        }
        
      } catch (error) {
        this.error = 'Erreur lors de l\'export'
        console.error('Erreur exportEmails:', error)
        throw error
      }
    },

    async reprocessEmail(emailId) {
      try {
        // Simulation d'appel API pour retraitement
        // await axios.post(`/api/emails/${emailId}/reprocess`)
        
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        const email = this.emails.find(e => e.id === emailId)
        if (email) {
          email.status = 'processed'
          email.aiScore = Math.min(email.aiScore + 15, 98)
          this.updateStats()
        }
        
      } catch (error) {
        this.error = 'Erreur lors du retraitement'
        console.error('Erreur reprocessEmail:', error)
        throw error
      }
    },

    clearError() {
      this.error = null
    },

    setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
    },

    clearFilters() {
      this.filters = {
        status: '',
        accountId: null,
        dateFrom: null,
        dateTo: null,
        sender: '',
        content: ''
      }
    },

    setPagination(page, limit = 50) {
      this.pagination.page = page
      this.pagination.limit = limit
    }
  }
})